/**
 * /product-discovery — SEO page for the discovery sub-cluster of
 * pm_discipline: "product discovery process/techniques/template/questions/
 * framework/tool/software" + "product discovery" (~800 aggregate, KD low,
 * strong fit). Genuine guide to discovery, then the Draftlize turn:
 * discovery produces evidence and assumptions; the failure is the line
 * from insight → decision → spec breaking. Draftlize makes discovery
 * findings cards the resulting decisions cite.
 *
 * English-only + inline. Chinese variant in ProductDiscoveryZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function ProductDiscovery() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/product-discovery"
      documentTitle="Product Discovery: Process, Techniques, Template · Draftlize"
      documentDescription="Product discovery is how teams decide what's worth building. The process, techniques, questions to ask, and a template — plus where the evidence trail breaks."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · Product discovery</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 62px)', lineHeight: 1.03, margin: '12px 0 24px' }}>
            Product discovery,<br/>
            <em>and the trail it leaves behind.</em>
          </h1>
          <p className="lede">
            <em>Product discovery</em> is the work of deciding what&apos;s worth building <em>before</em> you build it — talking to users, testing assumptions, and turning a vague request into a justified bet. It&apos;s the opposite of taking a feature request at face value and shipping it. This page covers the discovery process, the techniques that actually move things, the questions worth asking, and a simple template — then the part most guides skip: discovery produces a pile of evidence and assumptions, and the line from <em>insight → decision → spec</em> is exactly where it usually breaks.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">So, <em>what is product discovery?</em></h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            Product discovery is how a team reduces the risk that they build the wrong thing. It runs in parallel with delivery — while engineers ship what&apos;s already decided, the team is investigating what should come next: is the problem real, do people want this, can we build it, should we. The output isn&apos;t a feature; it&apos;s a <em>decision you can defend</em>, with the evidence attached.
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: 0 }}>
            The classic frame is &quot;dual-track&quot;: discovery and delivery as continuous, parallel streams, not phases. The risks discovery tackles are usually grouped as value (will they use it), usability (can they), feasibility (can we build it), and viability (should we, for the business). A good discovery process is just a disciplined way of retiring those risks before they cost a quarter of engineering.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The techniques</span>
            <h2 className="serif">What discovery <em>actually looks like.</em></h2>
          </div>
          <p>Five techniques cover most real discovery. You don&apos;t run all of them every time — you pick the ones that retire your biggest current risk.</p>
        </div>

        <div style={{ border: '1px solid var(--ink-0)', background: 'var(--paper-0)', fontFamily: "'Inter', sans-serif" }}>
          {[
            { k: 'Customer interviews', hint: 'Value risk', body: 'Talking to real users about their actual problems, not pitching your solution. The single highest-leverage technique — done continuously, not as a one-off study. The output is a clear-eyed view of the problem before any feature is on the table.' },
            { k: 'Opportunity solution tree', hint: 'Framing', body: 'Teresa Torres\'s map from a desired outcome down through opportunities (unmet needs) to candidate solutions. Keeps you from jumping to a feature before you\'ve understood the opportunity space — and makes the reasoning visible.' },
            { k: 'Assumption mapping', hint: 'Prioritize risk', body: 'List what must be true for an idea to work, then sort by how risky and how unknown each assumption is. You test the riskiest, least-known ones first. Stops you validating the comfortable assumptions and ignoring the load-bearing one.' },
            { k: 'Prototyping & tests', hint: 'Usability/feasibility', body: 'Low-fi prototypes, fake-door tests, concierge MVPs — the cheapest experiment that can disprove an assumption. The goal is a fast "no" before an expensive "yes", not a polished demo.' },
            { k: 'Data & analytics', hint: 'Evidence', body: 'Funnels, cohort retention, support themes — the quantitative half that interviews can\'t give you. Best paired with qualitative work: the numbers tell you where to look, the interviews tell you why.' },
          ].map((f, i) => (
            <div key={f.k} style={{ display: 'grid', gridTemplateColumns: '210px 1fr', gap: 24, padding: '20px 22px', borderTop: i === 0 ? 0 : '1px dashed var(--rule)', alignItems: 'baseline' }}>
              <div>
                <span className="serif" style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}>{f.k}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--amber)', fontWeight: 700 }}>{f.hint}</span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">A discovery <em>template &amp; the questions to ask.</em></h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            A lightweight discovery doc answers six things: the <strong>outcome</strong> you&apos;re chasing, the <strong>opportunity</strong> (the unmet need), the <strong>assumptions</strong> that must hold, the <strong>experiments</strong> run and what they showed, the <strong>decision</strong> reached, and the <strong>open questions</strong> left. That&apos;s the whole template — the value is in filling the decision and its evidence honestly.
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: 0 }}>
            The questions worth asking are the uncomfortable ones: <em>What would have to be true for this to be a bad idea? Who is this NOT for? What&apos;s the cheapest way to be wrong fast? If we&apos;re right, what breaks downstream?</em> Discovery that only confirms the plan isn&apos;t discovery.
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The evidence trail is where it <em>breaks.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Discovery&apos;s real output is a chain: an observation became an insight, the insight justified a decision, the decision shaped a spec. In most teams that chain lives in scattered docs and memory, so when an assumption is later disproven, nothing walks the chain forward to the spec built on it. Draftlize makes the chain explicit.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Findings become linked cards</h3>
            <p>An interview observation, an experiment result, an assumption — each is a card. The decision that rests on them links back to the evidence, so &quot;why did we decide this&quot; has an answer that isn&apos;t &quot;someone remembers a call from March.&quot;</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">An assumption fails, dependents flag stale</h3>
            <p>A later test disproves an assumption discovery relied on. The decision that cited it — and the spec built on that decision — auto-flag stale. The evidence trail runs forward, not just backward, so a disproven assumption doesn&apos;t leave a live spec quietly resting on it.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Your agent reads the evidence, not a summary</h3>
            <p>When Claude Code or Cursor drafts the spec over MCP, it reads the actual decision and the findings behind it — so what gets built traces to the discovery that justified it, instead of a lossy hand-off through a doc nobody re-opens.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Discovery&apos;s output isn&apos;t a feature — it&apos;s a decision with evidence attached. Lose the attachment and you&apos;ve kept the conclusion and thrown away the reason.
        </blockquote>
        <cite>Run discovery your way. Keep the chain from observation to spec intact.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What is the difference between product discovery and delivery?', a: 'Discovery decides what to build and why; delivery builds it. The modern practice is "dual-track" — they run continuously in parallel, not as sequential phases. Discovery retires the risk that you build the wrong thing; delivery executes once that risk is low enough.' },
            { q: 'What are the main product discovery techniques?', a: 'Continuous customer interviews, opportunity solution trees, assumption mapping, cheap prototypes and experiments (fake-door, concierge), and quantitative data analysis. You pick the technique that retires your biggest current risk rather than running all of them every time.' },
            { q: 'Who is involved in product discovery?', a: 'The classic "product trio" — product manager, designer, and engineer — discovering together, so value, usability, and feasibility are weighed at once. Pulling the engineer in early is what stops discovery from proposing things that can\'t be built affordably.' },
            { q: 'What questions should I ask in product discovery?', a: 'The uncomfortable ones: what would make this a bad idea, who is it explicitly not for, what is the cheapest way to find out we are wrong, and what breaks downstream if we are right. Discovery that only confirms the existing plan is not really discovery.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Keep your discovery evidence <em>connected — free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Interview, prototype, and analyze however you like. Put the findings and decisions in Draftlize as linked cards, so when an assumption is disproven, every decision and spec that rested on it flags stale — and your agent always builds from the evidence, not a stale summary.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
